// Gemeinsame Hülle für die interaktiven Rechner im Editor-Look.
// Layout: Vorschau-Canvas oben (mit Toolbar + Legende), darunter zwei Panels
// ("Maße fein justieren", "Bohrpunkte & Maße") und ein "Schritt für Schritt"-Block.
// Die eigentliche Geometrie kommt aus calc.js, die Drag-Interaktion ändert nur Eingaben.
import React from "react";
import { Minus, Plus, RotateCcw, Crosshair } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Warnings, ResultRow, ResultActions, Steps } from "./shared";

// Ein Eingabefeld für die Feinjustage. Größeres Feld, Einheit rechts.
function FineField({ field }) {
  return (
    <div>
      <Label htmlFor={`fine-${field.id}`} className="block text-sm font-medium mb-1.5">
        {field.label}
      </Label>
      <div className="relative">
        <Input
          id={`fine-${field.id}`}
          data-testid={`field-${field.id}`}
          type="number"
          inputMode="decimal"
          step={field.step || "0.5"}
          value={field.value}
          onChange={(e) => field.onChange(e.target.value)}
          className="h-11 pr-11 text-base tabular-nums"
        />
        {field.unit !== "" && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
            {field.unit || "cm"}
          </span>
        )}
      </div>
      {field.hint && <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed">{field.hint}</p>}
    </div>
  );
}

const SECTION_TITLE = "font-heading text-xs font-semibold uppercase tracking-wider text-muted-foreground";

export default function EditorShell({
  testId,
  canvas,
  canvasReady = true,
  emptyHint,
  toolbarHint,
  // optionale Toolbar-Steuerungen
  count, // { value, onInc, onDec, label } | null
  orient, // { value: "h"|"v", onH, onV } | null
  onCenter, // () => void | null
  onReset, // () => void
  fields = [],
  warnings = [],
  hasResult = false,
  results = [],
  steps = [],
  copyText = "",
}) {
  return (
    <div data-testid={testId} className="space-y-4">
      {/* Vorschau-Editor */}
      <Card className="overflow-hidden border p-0">
        {/* Toolbar */}
        <div className="no-print flex flex-wrap items-center justify-between gap-3 border-b bg-secondary/40 px-4 py-3">
          <span className="max-w-[46ch] text-xs leading-snug text-muted-foreground">{toolbarHint}</span>
          <div className="flex flex-wrap items-center gap-2">
            {count && (
              <div className="flex items-center gap-0.5 rounded-lg border bg-background p-0.5">
                <button
                  type="button"
                  onClick={count.onDec}
                  aria-label="Eins weniger"
                  className="flex h-7 w-7 items-center justify-center rounded-md text-primary hover:bg-secondary"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <span className="min-w-[74px] text-center text-xs font-semibold tabular-nums">
                  {count.value} {count.label}
                </span>
                <button
                  type="button"
                  onClick={count.onInc}
                  aria-label="Eins mehr"
                  className="flex h-7 w-7 items-center justify-center rounded-md text-primary hover:bg-secondary"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            )}
            {orient && (
              <div className="flex items-center gap-0.5 rounded-lg border bg-background p-0.5" data-testid="orient-toggle">
                <button
                  type="button"
                  onClick={orient.onH}
                  className={`h-7 rounded-md px-3 text-xs font-semibold ${
                    orient.value !== "v" ? "bg-primary text-primary-foreground" : "text-muted-foreground"
                  }`}
                >
                  Horizontal
                </button>
                <button
                  type="button"
                  onClick={orient.onV}
                  className={`h-7 rounded-md px-3 text-xs font-semibold ${
                    orient.value === "v" ? "bg-primary text-primary-foreground" : "text-muted-foreground"
                  }`}
                >
                  Vertikal
                </button>
              </div>
            )}
            {onCenter && (
              <Button type="button" variant="outline" size="sm" onClick={onCenter} className="h-8">
                <Crosshair className="mr-1.5 h-3.5 w-3.5" /> Mittig
              </Button>
            )}
            <Button type="button" variant="outline" size="sm" onClick={onReset} className="h-8" data-testid="btn-reset">
              <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Zurücksetzen
            </Button>
          </div>
        </div>

        {/* Canvas */}
        <div className="px-2 pb-1 pt-2 sm:px-4">
          {canvasReady ? (
            canvas
          ) : (
            <p className="py-16 text-center text-sm text-muted-foreground">{emptyHint}</p>
          )}
        </div>

        {/* Legende */}
        <div className="no-print flex flex-wrap gap-x-5 gap-y-2 border-t px-4 py-3">
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="inline-flex h-3.5 w-3.5 rounded-full border-[2.5px] border-accent bg-white" /> Bohrpunkt
          </span>
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="inline-flex h-4 w-4 rounded-full border-2 border-primary bg-white" /> Anfasser zum Ziehen
          </span>
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="inline-flex h-0 w-4 border-t-2 border-dashed border-muted-foreground" /> Hilfslinie
          </span>
        </div>
      </Card>

      {/* Panels: Feinjustage + Ergebnis */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card className="border p-4 sm:p-5">
          <h3 className={`${SECTION_TITLE} mb-4`}>Maße fein justieren</h3>
          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
            {fields.map((f) => (
              <FineField key={f.id} field={f} />
            ))}
          </div>
        </Card>

        <Card className="border p-4 sm:p-5">
          <h3 className={`${SECTION_TITLE} mb-3`}>Bohrpunkte &amp; Maße</h3>
          {warnings.length > 0 && <Warnings items={warnings} />}
          {hasResult ? (
            <div data-testid="calc-result" className={warnings.length > 0 ? "mt-3" : ""}>
              {results.map((row, i) => (
                <ResultRow key={i} label={row.label} value={row.value} />
              ))}
              <ResultActions text={copyText} />
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Gib die Maße ein, um die Bohrpunkte zu sehen.</p>
          )}
        </Card>
      </div>

      {/* Schritt für Schritt */}
      {hasResult && steps.length > 0 && (
        <Card className="border p-4 sm:p-5">
          <h3 className={`${SECTION_TITLE} mb-1`}>Schritt für Schritt</h3>
          <Steps items={steps} />
        </Card>
      )}

      <p className="px-1 pt-1 text-center text-[11px] leading-relaxed text-muted-foreground">
        Maße auf den Millimeter gerundet. Markierungen vor dem Bohren mit der Wasserwaage prüfen und auf Leitungen achten.
      </p>
    </div>
  );
}
